/**
 * Sortieren und Suchen in Personenlisten. Gewünscht nach der
 * Septemberabrechnung (Oktober 2026): nach Namen und nach Höhe des
 * Trinkgelds, von hoch nach tief und umgekehrt.
 *
 * Beträge kommen als String mit Punkt und gehen nie durch `Number`
 * (`format.ts`): verglichen wird über ganze Cent als BigInt.
 */
export type Richtung = "auf" | "ab";

/** `"1234.5"` → `123450n`; `null` für fehlende oder unlesbare Beträge. */
export function cent(betrag: string | null | undefined): bigint | null {
  if (betrag === null || betrag === undefined) return null;
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(betrag.trim());
  if (!m) return null;
  const [, vorzeichen, ganz = "0", nach = ""] = m;
  const c = BigInt(ganz) * 100n + BigInt(nach.padEnd(2, "0"));
  return vorzeichen === "-" ? -c : c;
}

/** Fehlende Beträge stehen in beiden Richtungen hinten. */
export function vergleicheBetrag(
  a: string | null | undefined,
  b: string | null | undefined,
  richtung: Richtung,
): number {
  const x = cent(a);
  const y = cent(b);
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  const d = x < y ? -1 : x > y ? 1 : 0;
  return richtung === "auf" ? d : -d;
}

/** Nach deutscher Schreibweise: „Ä“ bei „A“, Groß und klein gleich. */
export function vergleicheText(a: string, b: string, richtung: Richtung): number {
  const d = a.localeCompare(b, "de", { sensitivity: "base" });
  return richtung === "auf" ? d : -d;
}

export function vergleicheZahl(a: number | null, b: number | null, richtung: Richtung): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  return richtung === "auf" ? a - b : b - a;
}

/** Jedes Wort der Suche muss in einem der Felder vorkommen, ohne Rücksicht auf Groß und klein. */
export function passtZurSuche(
  suche: string,
  ...felder: (string | number | null | undefined)[]
): boolean {
  const worte = suche.toLocaleLowerCase("de").split(/\s+/).filter(Boolean);
  if (worte.length === 0) return true;
  const text = felder
    .filter((f) => f !== null && f !== undefined)
    .join(" ")
    .toLocaleLowerCase("de");
  return worte.every((w) => text.includes(w));
}
